import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BaseResponse } from 'src/shared/models/base-response.model';
import {
	ChangeLog,
	ChangeLogsResponse,
} from 'src/shared/models/change-log.model';
import {
	HousingSearch,
	HousingSearchResponse,
} from 'src/shared/models/housing-search.model';
import {
	AddHousingUnitResponse,
	GetHousingUnitResponse,
	HousingUnit,
} from 'src/shared/models/housing-unit.model';
import { Housing } from 'src/shared/models/housing.model';
import {
	UserDropDown,
	UserDropDownResponse,
} from 'src/shared/models/user-drop-down.model';
import { BackendService } from './backend.service';

const API_URL = `${environment.apiUrl}/web/housing`;

export const GET_HOUSING_URL = `${API_URL}/gethousing`;
export const GET_HOUSING_RESPONSE = 'Fetched Housing';

export const GET_TENANTS_URL = `${API_URL}/tenants`;
export const GET_TENANTS_RESPONSE = 'Fetched Tenants';

export const ADD_URL = `${API_URL}/add`;
export const ADD_RESPONSE = 'Added Housing Unit';

export const GET_HOUSING_UNIT_URL = `${API_URL}/gethousingunit`;
export const GET_HOUSING_UNIT_RESPONSE = 'Fetched Housing Unit';

export const GET_HOUSING_UNIT_CHANGE_LOGS_URL = `${API_URL}/gethousingunitchangelogs`;
export const GET_HOUSING_UNIT_CHANGE_LOGS_RESPONSE =
	'Fetched Housing Unit Change Logs';

export const SAVE_URL = `${API_URL}/save`;
export const SAVE_RESPONSE = 'Housing Unit Saved';

export const SAVE_USER_HOUSING_INFO_URL = `${API_URL}/saveuserhousinginfo`;
export const SAVE_USER_HOUSING_INFO_RESPONSE = 'Saved User Housing Info';

export const ARCHIVE_HOUSING_UNIT_URL = `${API_URL}/archivehousingunit`;
export const ARCHIVE_HOUSING_UNIT_RESPONSE = 'Archived Housing Unit';

export const ACTIVATE_HOUSING_UNIT_URL = `${API_URL}/activatehousingunit`;
export const ACTIVATE_HOUSING_UNIT_RESPONSE = 'Activated Housing Unit';

@Injectable({
	providedIn: 'root',
})
export class HousingBackendService {
	private backend = inject(BackendService);

	/**
	 * Get Housing
	 * ============
	 * API Endpoint: /web/housing/gethousing
	 * JSON Parameters: HousingSearch
	 *
	 * Returns the result of the search query based upon the provided search values
	 */
	getHousing(search: HousingSearch): Observable<HousingSearchResponse> {
		return this.backend
			.post(GET_HOUSING_URL, GET_HOUSING_RESPONSE, search)
			.pipe(map((r: HousingSearchResponse) => r));
	}

	/**
	 * Tenants
	 * ========
	 * API Endpoint: /web/housing/tenants
	 *
	 * Returns the current Housing Unit tenants
	 */
	getTenants(): Observable<UserDropDown[]> {
		return this.backend
			.post(GET_TENANTS_URL, GET_TENANTS_RESPONSE, {})
			.pipe(
				map((response: UserDropDownResponse) =>
					response.dropdowns.map((json) =>
						UserDropDown.fromJSON(json)
					)
				)
			);
	}

	/**
	 * Add Housing Unit
	 * =================
	 * API Endpoint: /web/housing/add
	 * JSON Parameters: HousingUnit
	 *
	 * Adds a new Housing Unit and returns an updated HousingUnit object
	 */
	addHousingUnit(
		housingUnit: HousingUnit
	): Observable<AddHousingUnitResponse> {
		return this.backend
			.post(ADD_URL, ADD_RESPONSE, housingUnit)
			.pipe(map((response: AddHousingUnitResponse) => response));
	}

	/**
	 * Get Housing Unit
	 * =================
	 * API Endpoint: /web/housing/gethousingunit
	 * JSON Parameters: id
	 *
	 * Returns the Housing Unit for the provided `housingUnitId`
	 */
	getHousingUnit(housingUnitId: number): Observable<HousingUnit> {
		return this.backend
			.post(GET_HOUSING_UNIT_URL, GET_HOUSING_UNIT_RESPONSE, {
				id: housingUnitId,
			})
			.pipe(
				map((response: GetHousingUnitResponse) =>
					HousingUnit.fromJSON(response.housingUnit)
				)
			);
	}

	/**
	 * Get Housing Unit Change Logs
	 * =============================
	 * API Endpoint: /web/housing/gethousingunitchangelogs
	 * JSON Parameters: id
	 *
	 * Returns the Housing Unit's Change Logs for the provided `housingUnitId`
	 */
	getHousingUnitChangeLogs(housingUnitId: number): Observable<ChangeLog[]> {
		return this.backend
			.post(
				GET_HOUSING_UNIT_CHANGE_LOGS_URL,
				GET_HOUSING_UNIT_CHANGE_LOGS_RESPONSE,
				{
					id: housingUnitId,
				}
			)
			.pipe(
				map((response: ChangeLogsResponse) =>
					response.changeLogs.map((changeLogJSON) =>
						ChangeLog.fromJSON(changeLogJSON)
					)
				)
			);
	}

	/**
	 * Save Housing Unit
	 * ==================
	 * API Endpoint: /web/housing/save
	 * JSON Parameters: HousingUnit
	 *
	 * Saves a Housing Unit and returns the updated HousingUnit object
	 */
	saveHousingUnit(housingUnit: HousingUnit): Observable<HousingUnit> {
		return this.backend
			.post(SAVE_URL, SAVE_RESPONSE, housingUnit)
			.pipe(
				map((response: GetHousingUnitResponse) =>
					HousingUnit.fromJSON(response.housingUnit)
				)
			);
	}

	/**
	 * Save User Housing Info
	 * =======================
	 * API Endpoint: /web/housing/saveuserhousinginfo
	 * JSON Parameters: id, housing: HousingInfo
	 *
	 * Saves the User Housing Info part of the form
	 */
	saveUserHousingInfo(
		userId: number,
		housing: Housing
	): Observable<BaseResponse> {
		return this.backend.post(
			SAVE_USER_HOUSING_INFO_URL,
			SAVE_USER_HOUSING_INFO_RESPONSE,
			{ id: userId, housing: housing }
		);
	}

	/**
	 * Archive Housing Unit
	 * =====================
	 * API Endpoint: /web/housing/archivehousingunit
	 * JSON Parameters: id
	 *
	 * Archives the Housing Unit and returns a the updated object
	 */
	archiveHousingUnit(housingUnitId: number): Observable<HousingUnit> {
		return this.backend
			.post(ARCHIVE_HOUSING_UNIT_URL, ARCHIVE_HOUSING_UNIT_RESPONSE, {
				id: housingUnitId,
			})
			.pipe(
				map((response: GetHousingUnitResponse) =>
					HousingUnit.fromJSON(response.housingUnit)
				)
			);
	}

	/**
	 * Activate Housing Unit
	 * ======================
	 * API Endpoint: /web/housing/activatehousingunit
	 * JSON Parameters: id
	 *
	 * Activates the Housing Unit and returns a the updated object
	 */
	activateHousingUnit(housingUnitId: number): Observable<HousingUnit> {
		return this.backend
			.post(ACTIVATE_HOUSING_UNIT_URL, ACTIVATE_HOUSING_UNIT_RESPONSE, {
				id: housingUnitId,
			})
			.pipe(
				map((response: GetHousingUnitResponse) =>
					HousingUnit.fromJSON(response.housingUnit)
				)
			);
	}
}
